import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, User, ChevronRight, Loader2, Filter } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  delivered: "#10B981",
  in_progress: "#F59E0B",
  broken: "#EF4444",
  not_started: "#94A3B8",
};

function DeliveryBar({ delivered, in_progress, broken, not_started, total }) {
  if (!total) return <div className="h-3 border border-black bg-gray-200 rounded-sm" />;
  const segments = [
    { count: delivered, color: STATUS_COLORS.delivered },
    { count: in_progress, color: STATUS_COLORS.in_progress },
    { count: broken, color: STATUS_COLORS.broken },
    { count: not_started, color: STATUS_COLORS.not_started },
  ];
  return (
    <div className="h-3 border border-black flex overflow-hidden rounded-sm">
      {segments.map((s, i) =>
        s.count > 0 ? (
          <div
            key={i}
            style={{ width: `${(s.count / total) * 100}%`, backgroundColor: s.color }}
          />
        ) : null
      )}
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <Trophy className="w-5 h-5" style={{ color: "#FFC82E" }} />;
  if (rank === 2) return <Medal className="w-5 h-5" style={{ color: "#94A3B8" }} />;
  if (rank === 3) return <Medal className="w-5 h-5" style={{ color: "#D97706" }} />;
  return (
    <span className="w-5 h-5 flex items-center justify-center text-xs font-black" style={{ color: "#64748B" }}>
      {rank}
    </span>
  );
}

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState("all");
  const [partyFilter, setPartyFilter] = useState("all");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "#334155" : "#000000";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (level !== "all") params.level = level;
    if (partyFilter !== "all") params.party_id = partyFilter;
    axios
      .get(`${API}/leaderboard`, { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [level, partyFilter]);

  const parties = [...new Set(data.map((p) => p.party_id))].map((pid) => {
    const p = data.find((x) => x.party_id === pid);
    return { id: pid, name: p.party_name, color: p.party_color };
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="leaderboard-page">
      {/* Hero */}
      <div className="border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="h-2" style={{ backgroundColor: "#FFC82E" }} />
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-8 h-8" style={{ color: "#FFC82E" }} />
            <h1
              className="text-4xl sm:text-5xl font-black text-white"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="leaderboard-title"
            >
              Promise Leaderboard
            </h1>
          </div>
          <p className="text-base" style={{ color: "#94A3B8" }}>
            Ranking Australia's politicians by how many of their campaign promises they have delivered.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Filters */}
        <div
          className="flex flex-wrap items-center gap-3 p-4 border-2 border-black mb-8"
          style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
          data-testid="leaderboard-filters"
        >
          <Filter className="w-4 h-4" style={{ color: textSec }} />
          <span className="text-sm font-black" style={{ color: text }}>Filter:</span>

          {/* Level */}
          <div className="flex gap-1">
            {["all", "federal", "state"].map((l) => (
              <button
                key={l}
                data-testid={`filter-level-${l}`}
                onClick={() => setLevel(l)}
                className="px-3 py-1.5 text-xs font-black border-2 border-black transition-all capitalize"
                style={{
                  backgroundColor: level === l ? "#FFC82E" : "transparent",
                  color: level === l ? "#000" : text,
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="h-5 border-l-2 border-black hidden sm:block" />

          {/* Party filter */}
          <select
            data-testid="filter-party"
            value={partyFilter}
            onChange={(e) => setPartyFilter(e.target.value)}
            className="text-xs font-bold border-2 border-black px-2 py-1.5 cursor-pointer"
            style={{ backgroundColor: surface, color: text }}
          >
            <option value="all">All Parties</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <span className="ml-auto text-xs font-bold" style={{ color: textSec }}>
            {data.length} politicians
          </span>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 text-xs font-bold" style={{ color: textSec }}>
          {[
            { label: "Delivered", color: STATUS_COLORS.delivered },
            { label: "In Progress", color: STATUS_COLORS.in_progress },
            { label: "Broken", color: STATUS_COLORS.broken },
            { label: "Not Started", color: STATUS_COLORS.not_started },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 border border-black" style={{ backgroundColor: s.color }} />
              {s.label}
            </div>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#FFC82E" }} />
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((politician) => (
              <button
                key={politician.id}
                data-testid={`leaderboard-row-${politician.id}`}
                onClick={() => navigate(`/politicians/${politician.id}`)}
                className="w-full text-left border-2 border-black p-4 hover:-translate-y-0.5 transition-all group"
                style={{
                  backgroundColor: surface,
                  boxShadow: "3px 3px 0 #000",
                }}
              >
                <div className="flex items-center gap-3">
                  {/* Rank */}
                  <div className="flex-shrink-0 w-7 flex justify-center">
                    <RankBadge rank={politician.rank} />
                  </div>

                  {/* Avatar */}
                  <div
                    className="flex-shrink-0 w-10 h-10 border-2 border-black flex items-center justify-center"
                    style={{ backgroundColor: politician.party_color + "20" }}
                  >
                    <User className="w-5 h-5" style={{ color: politician.party_color }} />
                  </div>

                  {/* Name & Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-black text-sm" style={{ color: text }}>
                        {politician.name}
                      </span>
                      <span
                        className="text-xs font-black px-1.5 py-0.5 border border-black"
                        style={{ backgroundColor: politician.party_color + "20", color: politician.party_color }}
                      >
                        {politician.party_name}
                      </span>
                      <span className="text-xs font-bold capitalize px-1.5 py-0.5 border border-black"
                        style={{ backgroundColor: isDark ? "#0F172A" : "#F1F5F9", color: textSec }}>
                        {politician.level}
                      </span>
                    </div>
                    <div className="text-xs mb-2" style={{ color: textSec }}>
                      {politician.role} · {politician.chamber} · {politician.state}
                    </div>

                    {/* Bar */}
                    <DeliveryBar
                      delivered={politician.delivered}
                      in_progress={politician.in_progress}
                      broken={politician.broken}
                      not_started={politician.not_started}
                      total={politician.total_promises}
                    />

                    {/* Promise counts */}
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs font-bold">
                      <span style={{ color: STATUS_COLORS.delivered }}>
                        {politician.delivered} delivered
                      </span>
                      <span style={{ color: STATUS_COLORS.in_progress }}>
                        {politician.in_progress} in progress
                      </span>
                      {politician.broken > 0 && (
                        <span style={{ color: STATUS_COLORS.broken }}>
                          {politician.broken} broken
                        </span>
                      )}
                      <span style={{ color: textSec }}>
                        {politician.total_promises} total
                      </span>
                    </div>
                  </div>

                  {/* Delivery % */}
                  <div className="flex-shrink-0 text-right">
                    <div
                      className="text-2xl font-black"
                      style={{
                        color: politician.delivery_pct >= 60
                          ? STATUS_COLORS.delivered
                          : politician.delivery_pct >= 30
                          ? STATUS_COLORS.in_progress
                          : STATUS_COLORS.broken,
                        fontFamily: "Outfit, sans-serif",
                      }}
                    >
                      {politician.delivery_pct}%
                    </div>
                    <div className="text-xs font-bold" style={{ color: textSec }}>delivery</div>
                  </div>

                  <ChevronRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" style={{ color: textSec }} />
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && data.length === 0 && (
          <div className="text-center py-16" style={{ color: textSec }}>
            <p className="text-lg font-black">No politicians found for this filter.</p>
          </div>
        )}

        {/* Info footer */}
        <div className="mt-8 p-4 border-2 border-black text-sm" style={{ backgroundColor: isDark ? "#1E293B" : "#FFF9E6" }}>
          <p className="font-black mb-1" style={{ color: text }}>About this ranking</p>
          <p className="text-xs" style={{ color: textSec }}>
            Rankings are calculated from each politician's publicly tracked campaign promises.
            Delivery rate = promises marked "Delivered" ÷ total promises.
            Data is based on publicly verified information. Click any politician to view their full profile and promise tracker.
          </p>
        </div>
      </div>
    </div>
  );
}
